import { Subscription } from "@common-models/subscription";
import { safeDispatch as dispatch } from '@common/state/state-provider';
import { UpdateEvent } from "@common/types/interface";
import { setLoaderProgressMessage } from "../reducers/ChatbotReducer";
import { DBSubscriptionThunks } from "../reducers/DBServiceThunks";
import { updateDocumentProgress } from "../reducers/KnowledgeBaseProgressSlice";
import { validateSubscriptionsThunk } from "../reducers/UserThunks";
import { isExtensionContext } from "../services/detect-context";
import { MQTTSubscriber } from "./MQTTSubscriber";
import { handleWhatsappInterfaceEvents, processMessage } from "./NotiferHandlers";
import { setConnectionStatus } from "./NotifierReducer";
import { NotifierConnectionStatus, UpdateEventMessageData } from "./types";

export class NotifierService {
  private static instance: NotifierService;
  private mqttSubscriber: MQTTSubscriber;
  private userId: string | null = null;
  private accessToken: string | null = null;

  private constructor() {
    this.mqttSubscriber = MQTTSubscriber.getInstance();
  }

  public static getInstance(): NotifierService {
    if (!NotifierService.instance) {
      NotifierService.instance = new NotifierService();
    }
    return NotifierService.instance;
  }

  public disconnect(): void {
    console.log('[Notifier] Disconnecting MQTT');
    this.mqttSubscriber.disconnect();
    this.userId = null;
    this.accessToken = null;
    dispatch(setConnectionStatus(NotifierConnectionStatus.DISCONNECTED));
  }

  public connect(accessToken: string): void {
    // Check if already connected with the same token
    if (this.mqttSubscriber.isConnected() && this.accessToken === accessToken) {
      console.log('[Notifier] MQTT already connected with same token');
      return;
    }

    console.log('[Notifier] Connecting via MQTT');
    this.accessToken = accessToken;
    
    // Extract user ID from JWT token
    this.userId = this.extractUserIdFromToken(accessToken);
    
    if (!this.userId) {
      console.error('[Notifier] Cannot extract user ID from access token');
      dispatch(setConnectionStatus(NotifierConnectionStatus.UNAUTHENTICATED));
      return;
    }

    dispatch(setConnectionStatus(NotifierConnectionStatus.CONNECTED));
    
    // Setup MQTT message handlers - the MQTTSubscriber will handle deduplication
    this.setupMQTTHandlers();
    
    // Connect to MQTT broker
    this.mqttSubscriber.connect(this.userId, accessToken);
    
    dispatch(setConnectionStatus(NotifierConnectionStatus.AUTHENTICATED));
  }

  public emit(event: string, data: any): void {
    // MQTT is subscribe-only for frontend, no emit needed
    console.log(`[Notifier] MQTT frontend is subscribe-only, cannot emit event [${event}]`);
  }

  private extractUserIdFromToken(token: string): string | null {
    try {
      // Decode JWT token to extract user ID
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);
      return payload.userId || payload.sub || payload.id || null;
    } catch (error) {
      console.error('[Notifier] Error decoding JWT token:', error);
      return null;
    }
  }

  private setupMQTTHandlers(): void {
    // Clear existing handlers first to avoid duplicates
    // Note: this will replace any existing handlers for these event types
    console.log('[Notifier] Setting up MQTT message handlers');

    // Handle sync-user-data events
    this.mqttSubscriber.onMessage("sync-user-data", async (updateEvent: UpdateEvent) => {
      console.log('[Notifier] Syncing user data via MQTT');
      const subscriptionListRes = await dispatch(DBSubscriptionThunks.find({}));
      const subscriptionList: Subscription[] = subscriptionListRes.payload;
      dispatch(validateSubscriptionsThunk(subscriptionList));
    });

    // Handle chatbot progress updates  
    this.mqttSubscriber.onMessage("chatbot-progress", (progressEvent) => {
      console.log('[Notifier] Received chatbot progress update via MQTT:', progressEvent);
      if (progressEvent && progressEvent.message) {
        dispatch(setLoaderProgressMessage(progressEvent.message));
      }
    });

    // Handle document processing progress
    this.mqttSubscriber.onMessage("document-processing-progress", (progressEvent) => {
      console.log('[Notifier] Received document processing progress via MQTT:', progressEvent);
      dispatch(updateDocumentProgress(progressEvent));
    });

    // Handle general update events - directly process them without queueing
    this.mqttSubscriber.onMessage("update", async (eventMessage: UpdateEventMessageData) => {
      console.log('[Notifier] Received update event via MQTT:', eventMessage);
      
      // Handle whatsapp interface events separately
      if (eventMessage.path === 'whatsapp-interface') {
        if (isExtensionContext()) {
          await handleWhatsappInterfaceEvents(eventMessage, undefined);
        }
        return;
      }
      
      // Process entity updates directly - no need for message recovery with MQTT
      processMessage(eventMessage);
    });
  }
} 